import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  createParticipantLink,
  getMyParticipantLinks,
  updateParticipantLink,
  deleteParticipantLink,
  getLinkSubmissions,
  downloadSubmissionZip,
  mergeParticipantDocuments,
  mergeCustomFiles,
} from '../services/api';
import { ListSkeleton } from '../components/SkeletonLoader';

const DOC_TYPES = [
  { key: 'letter_to_parents', label: 'AA-OSAS-1.03F8 Letter to Parents', short: 'F8 Parents' },
  { key: 'risk_assessment', label: 'AA-OSAS-1.03F10 Risk Assessment', short: 'F10 Risk' },
  { key: 'insurance', label: 'Insurance', short: 'Insurance' },
  { key: 'moa', label: 'MOA', short: 'MOA' },
  { key: 'medical_clearance', label: 'Medical Clearance (UHS)', short: 'Medical (UHS)' },
];

const MOA_OPTIONS = [
  { value: 'academic', label: 'Academic Activity within Region IV-A (F11a)' },
  { value: 'nonacademic', label: 'Non-academic Activity within Region IV-A (F11b)' },
  { value: 'outside', label: 'Activity outside Region IV-A (F11c)' },
];

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);
const DownloadIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);
const ExternalIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);
const formatDateSafe = (dateVal) => {
  if (!dateVal) return '—';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDateTimeSafe = (dateVal) => {
  if (!dateVal) return '—';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-PH', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const buildPortalUrl = (token) => {
  if (typeof window === 'undefined' || !token) return '#';
  return `${window.location.origin}/#/participant/${token}`;
};

const getLinkStatus = (link) => {
  if (!link || !link.is_active) return { label: 'Deactivated', color: 'bg-gray-100 text-gray-500' };
  if (link.is_expired) return { label: 'Expired', color: 'bg-red-100 text-red-600' };
  return { label: 'Active', color: 'bg-green-100 text-green-700' };
};

// ─── Single Link Card (with inline submissions) ───────────────────────────────
const LinkCard = ({ link, onToggleActive, onDelete, onRefresh }) => {
  const [copied, setCopied] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [mergingKey, setMergingKey] = useState(null);

  const status = getLinkStatus(link);
  const url = buildPortalUrl(link?.token);

  const loadSubmissions = async () => {
    if (!link?.link_id) return;
    setLoading(true);
    try {
      const res = await getLinkSubmissions(link.link_id);
      setSubmissions(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load submissions on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSubmissions(); }, [link?.link_id]);

  const handleCopy = () => {
    if (!url || url === '#') return;
    navigator.clipboard?.writeText?.(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMergeDownload = async (docType) => {
    if (!link?.link_id) return;
    setMergingKey(docType);
    try {
      const res = await mergeParticipantDocuments(link.link_id, docType);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const dtObj = DOC_TYPES.find((d) => d.key === docType);
      const safeActivity = (link?.activity_name || 'Activity').replace(/[^\w\-]/g, '_');
      a.download = `${(dtObj?.label || docType).replace(/[^\w\-]/g, '_')}_${safeActivity}_Compiled.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert('Failed to compile PDF or no submitted files found for this requirement.');
    } finally {
      setMergingKey(null);
    }
  };

  const handleBulkDownload = async (docType) => {
    if (!link?.link_id) return;
    setDownloadingKey(docType);
    try {
      const res = await downloadSubmissionZip(link.link_id, docType);
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${docType}_${(link?.activity_name || 'activity').replace(/\s+/g, '_')}.zip`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { /* ignore */ } finally {
      setDownloadingKey(null);
    }
  };

  const safeSubmissions = Array.isArray(submissions) ? submissions : [];
  const completedCount = safeSubmissions.filter(s =>
    DOC_TYPES.every(dt => s?.documents?.[dt.key])
  ).length;

  return (
    <div className="bg-white rounded-[16px] border border-gray-200 shadow-sm overflow-hidden">
      {/* Link summary row */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Title + badge */}
            <div className="flex items-center space-x-2 mb-1.5">
              <h3 className="font-bold text-gray-900 text-[15px] truncate">{link?.activity_name || 'Untitled Activity'}</h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${status.color}`}>
                {status.label}
              </span>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mb-3">
              <span>MOA: {link?.moa_label || '—'}</span>
              {link?.lock_date && <span> <strong>Deadline:</strong> {formatDateSafe(link.lock_date)}{link.lock_time ? ' ' + link.lock_time : ''}</span>}
              <span><strong> {safeSubmissions.length} </strong>participant{safeSubmissions.length !== 1 ? 's' : ''}{safeSubmissions.length > 0 ? ` · ${completedCount} Complete` : ''}</span>
              <span> <strong>Created</strong> {formatDateSafe(link?.created_at)}</span>
            </div>

            {/* URL row */}
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-[11px] text-gray-500 truncate flex-1 font-mono">{url}</span>
              <button
                onClick={handleCopy}
                className={`flex items-center space-x-1 text-[10px] font-bold px-2 py-1 rounded transition-all flex-shrink-0 ${copied ? 'text-green-600 bg-green-50' : 'text-[#086C53] hover:bg-[#086C53]/5'}`}
              >
                <CopyIcon />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <a href={url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Open portal">
                <ExternalIcon />
              </a>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex flex-col space-y-2 flex-shrink-0">
            {link.is_active && (
              <button
                onClick={() => onToggleActive(link)}
                className="text-[11px] font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                Deactivate
              </button>
            )}
            <button
              onClick={() => onDelete(link.link_id)}
              className="text-[11px] font-bold text-red-500 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* ── Built-in PDF Merger for Requirement Uploads ── */}
      <div className="px-5 py-3.5 bg-[#f0faf6] border-t border-b border-[#d1fae5] flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center space-x-2">
            <span className="text-[12px] font-black text-[#065440] uppercase tracking-wide flex items-center space-x-1">
              <svg className="w-4 h-4 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
              <span>Download Compiled PDF</span>
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-1.5 flex-wrap gap-y-1.5">
          {DOC_TYPES.map(dt => (
            <button
              key={dt.key}
              onClick={() => handleMergeDownload(dt.key)}
              disabled={!!mergingKey || submissions.length === 0}
              title={`Merge all participants' ${dt.label} into 1 compiled PDF`}
              className="flex items-center space-x-1.5 bg-[#086C53] hover:bg-[#065440] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {mergingKey === dt.key ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              <span>{dt.short} PDF</span>
            </button>
          ))}
        </div>
      </div>

      {/* Always-visible submissions section */}
      <div>
        {/* Secondary raw ZIP download bar */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center space-x-2 flex-wrap gap-y-2 bg-gray-50/70">
          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Raw Files (ZIP):</span>
          {DOC_TYPES.map(dt => (
            <button
              key={dt.key}
              onClick={() => handleBulkDownload(dt.key)}
              disabled={!!downloadingKey || submissions.length === 0}
              title={`Download all ${dt.label} as raw ZIP`}
              className="flex items-center space-x-1 bg-white border border-gray-200 hover:bg-gray-100 text-gray-600 text-[10px] font-semibold px-2 py-1 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingKey === dt.key
                ? <div className="w-2.5 h-2.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                : <DownloadIcon />}
              <span>{dt.short}</span>
            </button>
          ))}
          <button
            onClick={loadSubmissions}
            disabled={loading}
            className="ml-auto text-[10px] font-bold text-[#086C53] hover:underline disabled:opacity-40 cursor-pointer"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-6 text-center">
            <div className="w-6 h-6 border-4 border-gray-200 border-t-[#086C53] rounded-full animate-spin mx-auto mb-2" />
            <p className="text-gray-400 text-xs">Loading submissions…</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-400 text-sm font-medium">No submissions yet.</p>
            <p className="text-gray-300 text-xs mt-1">Share the link with participants to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">SLSU Email</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">Submitted At</th>
                  {DOC_TYPES.map(dt => (
                    <th key={dt.key} className="text-center px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      {dt.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {submissions.map((sub, idx) => (
                  <tr key={sub.submission_id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 text-gray-300 text-xs font-medium">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 text-[13px] whitespace-nowrap">{sub.full_name}</p>
                      {sub.program && <p className="text-[10px] text-gray-400">{sub.program}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{sub.slsu_email}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatDateTimeSafe(sub.submitted_at)}
                    </td>
                    {DOC_TYPES.map(dt => {
                      const doc = sub?.documents?.[dt.key];
                      const docUrl = typeof doc === 'string' ? doc : (doc?.file_path || '');
                      return (
                        <td key={dt.key} className="px-3 py-3 text-center">
                          {docUrl ? (
                            <a
                              href={docUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="View file"
                              className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </a>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-300 rounded-full">
                              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const OrgParticipants = () => {
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newForm, setNewForm] = useState({ activity_name: '', moa_variant: 'academic', lock_date: '', lock_time: '23:59' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [deletingLoading, setDeletingLoading] = useState(false);

  const [showMergeTool, setShowMergeTool] = useState(false);
  const [customFiles, setCustomFiles] = useState([]);
  const [customMerging, setCustomMerging] = useState(false);
  const [customMergeError, setCustomMergeError] = useState('');
  const [outputFileName, setOutputFileName] = useState('Compiled_Participant_Requirement');

  const fetchLinks = async () => {
    try {
      setLinksLoading(true);
      setFetchError('');
      const res = await getMyParticipantLinks();
      if (typeof res.data === 'string' && res.data.includes('<!DOCTYPE html>')) {
        throw new Error('API returned HTML - proxy misconfigured. Check Nginx /participant proxy.');
      }
      setLinks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '';
      if (msg) setFetchError(msg);
      setLinks([]);
    } finally {
      setLinksLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const handleCustomMerge = async (e) => {
    e.preventDefault();
    if (customFiles.length === 0) return;
    setCustomMerging(true);
    setCustomMergeError('');
    try {
      const formData = new FormData();
      customFiles.forEach((file) => formData.append('files', file));
      formData.append('output_filename', outputFileName || 'Compiled_Participant_Requirement.pdf');

      const res = await mergeCustomFiles(formData);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const safeName = (outputFileName || 'Compiled_Participant_Requirement').replace(/[^\w\-]/g, '_');
      a.download = `${safeName}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      setShowMergeTool(false);
      setCustomFiles([]);
    } catch (err) {
      setCustomMergeError(err?.response?.data?.detail || 'Failed to merge selected files into PDF.');
    } finally {
      setCustomMerging(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.activity_name.trim()) { setCreateError('Activity name is required.'); return; }
    setCreateError('');
    setCreating(true);
    try {
      await createParticipantLink({
        activity_name: newForm.activity_name,
        moa_variant: newForm.moa_variant,
        lock_date: newForm.lock_date || null,
        lock_time: newForm.lock_time || null,
      });
      setShowCreateForm(false);
      setNewForm({ activity_name: '', moa_variant: 'academic', lock_date: '', lock_time: '23:59' });
      fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to create link.';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (link) => {
    try {
      await updateParticipantLink(link.link_id, { is_active: !link.is_active });
      fetchLinks();
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    setDeletingLoading(true);
    try {
      await deleteParticipantLink(deletingLinkId);
      setDeletingLinkId(null);
      fetchLinks();
    } catch { /* ignore */ } finally {
      setDeletingLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Participant Submissions</h2>
          <p className="text-gray-500 text-sm mt-0.5"></p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowMergeTool(true)}
            className="border border-[#086C53] bg-[#086C53]/5 text-[#086C53] text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-[#086C53]/15 transition-colors shadow-xs flex items-center space-x-2 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
            <span>Merge Files</span>
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-[#086C53] text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-[#065440] transition-colors shadow-sm flex items-center space-x-2 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            <span>Generate Link</span>
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>Failed to load links: {fetchError}</span>
          <button onClick={fetchLinks} className="ml-3 text-xs font-bold bg-white border border-red-200 px-3 py-1 rounded-lg hover:bg-red-100">Retry</button>
        </div>
      )}
      {/* Links list */}
      {linksLoading ? (
        <ListSkeleton rows={3} />
      ) : !Array.isArray(links) || links.length === 0 ? (
        <div className="bg-white rounded-[16px] border border-gray-200 p-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <p className="text-gray-600 font-bold">No links yet</p>
          <p className="text-gray-400 text-sm mt-1">Click "Generate Link" to create your first participant submission link.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(Array.isArray(links) ? links : []).map(link => link?.link_id ? (
            <LinkCard
              key={link.link_id}
              link={link}
              onToggleActive={handleToggleActive}
              onDelete={(id) => setDeletingLinkId(id)}
              onRefresh={fetchLinks}
            />
          ) : null)}
        </div>
      )}

      {/* ── Create Link Modal ─────────────────────────────────────────────────── */}
      {showCreateForm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!creating) setShowCreateForm(false); }}
          />
          <div className="relative bg-white rounded-[24px] shadow-2xl w-full max-w-[460px] z-10">
            <div className="flex items-center justify-between px-7 pt-7 pb-4">
              <h3 className="text-[18px] font-bold text-gray-900">Generate Participant Link</h3>
              <button
                onClick={() => { if (!creating) setShowCreateForm(false); }}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="px-7 pb-7 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Activity Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Mountaineering Expedition 2025"
                  value={newForm.activity_name}
                  onChange={e => setNewForm(f => ({ ...f, activity_name: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#086C53] focus:ring-1 focus:ring-[#086C53]/30 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  MOA Variant (Pre-selected for participants) <span className="text-red-500">*</span>
                </label>
                <select
                  value={newForm.moa_variant}
                  onChange={e => setNewForm(f => ({ ...f, moa_variant: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#086C53] bg-white cursor-pointer"
                >
                  {MOA_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Deadline Date</label>
                  <input
                    type="date"
                    value={newForm.lock_date}
                    onChange={e => setNewForm(f => ({ ...f, lock_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#086C53] bg-white cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Deadline Time</label>
                  <input
                    type="time"
                    value={newForm.lock_time}
                    onChange={e => setNewForm(f => ({ ...f, lock_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#086C53] bg-white cursor-pointer"
                  />
                </div>
              </div>

              {createError && (
                <p className="text-red-500 text-xs font-medium bg-red-50 rounded-lg px-3 py-2">{createError}</p>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { if (!creating) setShowCreateForm(false); }}
                  className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-[#086C53] text-white rounded-xl py-2.5 text-sm font-bold hover:bg-[#065440] transition-colors disabled:opacity-60 cursor-pointer"
                >
                  {creating ? 'Generating…' : 'Generate Link'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Built-in PDF & Image Merger Tool Modal ─────────────────────────────── */}
      {showMergeTool && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!customMerging) setShowMergeTool(false); }}
          />
          <div className="relative bg-white rounded-[24px] shadow-2xl w-full max-w-[520px] z-10 overflow-hidden">
            <div className="flex items-center justify-between px-7 pt-7 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-[18px] font-bold text-gray-900 flex items-center space-x-2">
                  <span className="w-8 h-8 rounded-lg bg-[#e0f5ee] text-[#086C53] flex items-center justify-center font-black text-sm">
                    📄
                  </span>
                  <span>PDF & Image Merger</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Combine multiple PDF files and images into 1 single compiled PDF</p>
              </div>
              <button
                onClick={() => { if (!customMerging) setShowMergeTool(false); }}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCustomMerge} className="p-7 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Output Compiled File Name:
                </label>
                <input
                  type="text"
                  value={outputFileName}
                  onChange={(e) => setOutputFileName(e.target.value)}
                  placeholder="e.g. Letter_to_Parents_Compiled"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#086C53]"
                  required
                />
              </div>

              {/* Drag and Drop Zone */}
              <div
                onClick={() => document.getElementById('custom-merger-input').click()}
                className="border-2 border-dashed border-[#086C53]/40 bg-[#086C53]/5 hover:bg-[#086C53]/10 rounded-2xl p-6 text-center cursor-pointer transition-colors"
              >
                <svg className="w-8 h-8 text-[#086C53] mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-xs font-bold text-[#086C53]">Click to select PDF or image files to merge</p>
                <p className="text-[11px] text-gray-400 mt-1">Supports PDF, PNG, JPG, JPEG, WEBP</p>
                <input
                  id="custom-merger-input"
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const chosen = Array.from(e.target.files);
                      setCustomFiles((prev) => [...prev, ...chosen]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              {/* Selected Files List */}
              {customFiles.length > 0 && (
                <div className="space-y-1.5 max-h-[140px] overflow-y-auto custom-scrollbar pr-1">
                  <div className="flex items-center justify-between text-xs font-bold text-gray-600 px-1">
                    <span>Selected Files ({customFiles.length}):</span>
                    <button
                      type="button"
                      onClick={() => setCustomFiles([])}
                      className="text-red-500 hover:text-red-700 font-semibold cursor-pointer"
                    >
                      Clear
                    </button>
                  </div>
                  {customFiles.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                      <span className="truncate max-w-[360px] font-medium text-gray-700">{idx + 1}. {f.name}</span>
                      <button
                        type="button"
                        onClick={() => setCustomFiles((prev) => prev.filter((_, i) => i !== idx))}
                        className="text-gray-400 hover:text-red-500 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {customMergeError && (
                <div className="bg-red-50 text-red-600 rounded-lg p-2.5 text-xs font-medium border border-red-200">
                  {customMergeError}
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { if (!customMerging) setShowMergeTool(false); }}
                  className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={customMerging || customFiles.length === 0}
                  className="flex-1 bg-[#086C53] text-white rounded-xl py-2.5 text-sm font-bold hover:bg-[#065440] transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center space-x-1.5"
                >
                  {customMerging ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Merging…</span>
                    </>
                  ) : (
                    <span>Merge {customFiles.length > 0 ? `(${customFiles.length})` : ''} to PDF</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {deletingLinkId && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!deletingLoading) setDeletingLinkId(null); }}
          />
          <div className="relative bg-white rounded-[24px] shadow-2xl w-full max-w-[380px] p-8 text-center z-10">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-[17px] font-bold text-gray-900 mb-2">Delete This Link?</h3>
            <p className="text-gray-500 text-sm mb-6">This will permanently delete the link and all participant submissions. This cannot be undone.</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeletingLinkId(null)}
                disabled={deletingLoading}
                className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deletingLoading}
                className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-60"
              >
                {deletingLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrgParticipants;

